'use client';
import { useState, useEffect, useCallback, useRef } from 'react';
import type { Theme, Mode } from '../types/theme.types';
import { THEMES } from '../types/theme.types';
import { useThemeService } from '../providers/ThemeProvider';

const isBrowser = typeof globalThis.window !== 'undefined';

export interface UseThemeReturn {
	theme: Theme;
	mode: Mode;
	changeTheme: (theme: Theme) => void;
	changeMode: (mode: Mode) => void;
	toggleMode: () => void;
	isDark: boolean;
	isLight: boolean;
	availableThemes: typeof THEMES;
}

export function useTheme(): UseThemeReturn {
	const service = useThemeService();

	const [theme, setTheme] = useState<Theme>(() => service.getTheme());
	const [mode, setMode] = useState<Mode>(() => service.getMode());

	const cleanupRef = useRef<(() => void) | null>(null);

	const changeTheme = useCallback(
		(newTheme: Theme) => {
			service.setTheme(newTheme);
			setTheme(newTheme);
		},
		[service]
	);

	const changeMode = useCallback(
		(newMode: Mode) => {
			service.setMode(newMode);
			setMode(newMode);
		},
		[service]
	);

	const toggleMode = useCallback(() => {
		const newMode = service.toggleMode();
		setMode(newMode);
	}, [service]);

	useEffect(() => {
		if (!isBrowser) return;

		const observer = new MutationObserver(() => {
			setMode(service.getMode());
			setTheme(service.getTheme());
		});

		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['data-mode', 'data-theme'],
		});

		return () => observer.disconnect();
	}, [service]);

	useEffect(() => {
		if (!isBrowser) return;

		const currentMode = service.getMode();
		const systemPreference = service.getSystemPreference();

		if (currentMode !== systemPreference) {
			return;
		}

		if (cleanupRef.current) {
			cleanupRef.current();
		}

		cleanupRef.current = service.watchSystemPreference((systemMode) => {
			service.setMode(systemMode);
			setMode(systemMode);
		});

		return () => {
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
		};
	}, [service]);

	return {
		theme,
		mode,
		changeTheme,
		changeMode,
		toggleMode,
		isDark: mode === 'dark',
		isLight: mode === 'light',
		availableThemes: THEMES,
	};
}
